import { useEffect, useRef } from "react";
import { cache } from "../utils";
import { useNavigate } from "react-router";
import { APP_INACTIVITY_TIMEOUT } from "../utils/constant";

const INACTIVITY_TIMEOUT_MS = APP_INACTIVITY_TIMEOUT * 1000;
const LAST_ACTIVITY_KEY = "LAST_ACTIVITY_TIMESTAMP";

export const useInactivityManager = ({
  onLogout,
}: { onLogout?: () => void } = {}) => {
  const navigate = useNavigate();
  const inactivityTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const logoutUser = () => {
    cache.del("TOKEN");
    cache.del("USER");
    cache.del("APP_IS_ACTIVE");
    cache.del(LAST_ACTIVITY_KEY);

    if (onLogout) onLogout();
    else navigate("/", { replace: true });
  };

  // ---- Store timestamp and reset desktop inactivity timer
  const resetInactivityTimer = () => {
    const now = Date.now();
    cache.set(LAST_ACTIVITY_KEY, now);

    if (inactivityTimeout.current) clearTimeout(inactivityTimeout.current);

    inactivityTimeout.current = setTimeout(() => {
      logoutUser();
    }, INACTIVITY_TIMEOUT_MS);

    cache.set("APP_IS_ACTIVE", true);
  };

  // ---- Check if mobile/tab sleep exceeded timeout */
  const checkInactivityOnResume = () => {
  const lastActivity = Number(cache.get(LAST_ACTIVITY_KEY));
  if (!lastActivity || isNaN(lastActivity)) {
    // No valid timestamp yet — treat as just active
    resetInactivityTimer();
    return;
  }

  const now = Date.now();
  const inactiveDuration = now - lastActivity;

  // Defensive: ignore negative or unrealistic durations
  if (inactiveDuration < 0 || inactiveDuration >= INACTIVITY_TIMEOUT_MS) {
    logoutUser();
  } else {
    resetInactivityTimer();
  }
};


  useEffect(() => {
    const activityEvents = [
      "mousemove",
      "keydown",
      "click",
      "scroll",
      "touchstart",
    ];

    const handleActivity = () => {
      resetInactivityTimer();
    };

    resetInactivityTimer();

    activityEvents.forEach((event) =>
      window.addEventListener(event, handleActivity)
    );

    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") {
        checkInactivityOnResume();
      }
    });

    return () => {
      activityEvents.forEach((event) =>
        window.removeEventListener(event, handleActivity)
      );
      document.removeEventListener("visibilitychange", checkInactivityOnResume);
      if (inactivityTimeout.current) clearTimeout(inactivityTimeout.current);
    };
  }, []);

  return null;
};